// Settings Screen - Full-Width Responsive Layout
import { useState } from 'react';
import { useApp } from '../hooks/useApp';
import { useTheme } from '../config/theme.config.jsx';
import { storage } from '../services/storage';
import { testConnection } from '../services/openrouter';
import { MODEL_LIST, getWorkerUrl, clearAuth } from '../config/api.config';
import { SCREENS } from '../config/constants';
import Logger from '../services/logger';
import { Container, Text, Card, Button, IconButton } from '../components/ui';
import Icons from '../components/Icons';
import BottomNav from '../components/BottomNav';

function SettingsScreen() {
    const { goBack, showToast, setCurrentScreen } = useApp();
    const { theme, toggleTheme, isDark } = useTheme();
    const [selectedModel, setSelectedModelState] = useState(storage.getSelectedModel());
    const [showDebugPanel, setShowDebugPanel] = useState(false);
    const [testing, setTesting] = useState(false);

    const handleModelChange = (modelId) => {
        setSelectedModelState(modelId);
        storage.setSelectedModel(modelId);
        showToast('Đã đổi: ' + MODEL_LIST.find(m => m.id === modelId)?.name, 'success');
    };

    const handleTestConnection = async () => {
        setTesting(true);
        const result = await testConnection();
        setTesting(false);
        showToast(result.message, result.success ? 'success' : 'error');
    };

    const handleLogout = () => {
        if (confirm('Đăng xuất khỏi ứng dụng?')) {
            clearAuth();
            showToast('Đã đăng xuất', 'success');
            window.location.reload();
        }
    };

    const handleClearData = () => {
        if (confirm('Xóa toàn bộ lịch sử kiểm tra?')) {
            localStorage.removeItem('analysisHistory');
            showToast('Đã xóa lịch sử', 'success');
        }
    };

    return (
        <Container className="min-h-screen bg-theme-bg pb-32" safe>
            {/* Header - Full Width */}
            <div className="header-fullwidth border-b border-theme">
                <div className="header-content">
                    <IconButton onClick={goBack}><Icons.Back /></IconButton>
                    <Text className="font-semibold sm:text-lg">Cài đặt</Text>
                    <div className="w-11" />
                </div>
            </div>

            <div className="main-content py-4 sm:py-6 space-y-5 sm:space-y-6">
                {/* Theme Toggle */}
                <div>
                    <Text variant="small" className="uppercase tracking-wider mb-3">Giao diện</Text>
                    <Card>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-xl">{isDark ? '🌙' : '☀️'}</span>
                                <div>
                                    <Text className="font-medium text-sm block">Chế độ {isDark ? 'tối' : 'sáng'}</Text>
                                    <Text variant="small" className="mt-0.5">Thay đổi giao diện ứng dụng</Text>
                                </div>
                            </div>
                            <button
                                onClick={toggleTheme}
                                className={`w-14 h-8 rounded-full ${isDark ? 'bg-primary' : 'bg-gray-400'} relative transition-colors flex-shrink-0 touch-target`}
                            >
                                <div className={`absolute top-1 ${isDark ? 'right-1' : 'left-1'} w-6 h-6 bg-white rounded-full shadow transition-all`} />
                            </button>
                        </div>
                    </Card>
                </div>

                {/* AI Model Selection */}
                <div>
                    <Text variant="small" className="uppercase tracking-wider mb-3">Trí tuệ nhân tạo</Text>
                    <Card className="!p-4">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium mb-2">Model phân tích</label>
                                <div className="relative">
                                    <select
                                        value={selectedModel}
                                        onChange={(e) => handleModelChange(e.target.value)}
                                        className="w-full appearance-none bg-theme-secondary border border-theme rounded-xl px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-primary transition-all text-sm sm:text-base"
                                    >
                                        {MODEL_LIST.map((model) => (
                                            <option key={model.id} value={model.id}>
                                                {model.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-theme-tertiary">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Model Description */}
                            <div className="bg-theme-bg rounded-lg p-3 border border-theme">
                                <Text variant="small" className="text-theme-tertiary block mb-1">Mô tả:</Text>
                                <Text className="text-sm">
                                    {MODEL_LIST.find(m => m.id === selectedModel)?.desc || 'Model mặc định'}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Connection Status */}
                <div>
                    <Text variant="small" className="uppercase tracking-wider mb-3">Kết nối</Text>
                    <Card>
                        <div className="flex items-center justify-between">
                            <div>
                                <Text className="font-medium text-sm block">Trạng thái máy chủ</Text>
                                <Text variant="small" className="mt-0.5">
                                    {getWorkerUrl().includes('localhost') ? '🟡 Chế độ phát triển' : '🟢 Hoạt động'}
                                </Text>
                            </div>
                            <Button variant="secondary" size="sm" onClick={handleTestConnection} disabled={testing}>
                                {testing ? '...' : 'Kiểm tra'}
                            </Button>
                        </div>
                    </Card>
                </div>

                {/* Developer Section */}
                <div>
                    <button
                        onClick={() => setShowDebugPanel(!showDebugPanel)}
                        className="w-full flex items-center justify-between py-2"
                    >
                        <Text variant="small" className="uppercase tracking-wider">Dành cho nhà phát triển</Text>
                        <span className="text-theme-tertiary">{showDebugPanel ? '▼' : '▶'}</span>
                    </button>
                    {showDebugPanel && (
                        <Card className="mt-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Text className="font-medium text-sm block">Nhật ký hệ thống</Text>
                                    <Text variant="small" className="mt-0.5">{Logger.logs?.length || 0} bản ghi</Text>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => { Logger.exportLogs(); showToast('Đã xuất', 'success'); }}>
                                        Xuất
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => { Logger.clear(); showToast('Đã xóa', 'success'); }}>
                                        Xóa
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>

                {/* Version Info */}
                <Card>
                    <div className="flex justify-between">
                        <Text variant="caption">Phiên bản</Text>
                        <Text className="font-medium text-sm">v2.2.0</Text>
                    </div>
                </Card>

                {/* Account Actions */}
                <div className="space-y-3 pb-8">
                    <Button variant="secondary" onClick={handleClearData} className="w-full">
                        Xóa lịch sử kiểm tra
                    </Button>
                    <Button variant="danger" onClick={handleLogout} className="w-full">
                        Đăng xuất
                    </Button>
                </div>
            </div>

            <BottomNav />
        </Container>
    );
}

export default SettingsScreen;
